// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "./userStorage";
/**
 * Bible Reader Service
 *
 * Bible text is loaded only from validated, versioned local datasets.
 * Missing or unsupported content throws an explicit error; Scripture is never generated.
 */

import { bibleStructure, chapterExists, getBookName } from '../data/bibleData';
import {
  DEFAULT_BIBLE_TRANSLATION,
  getBibleTranslation
} from '../data/bibleTranslations';

const bookCache = new Map();
const manifestCache = new Map();

const STORAGE_KEYS = {
  BOOKMARKS: 'bible_reader_bookmarks',
  HIGHLIGHTS: 'bible_reader_highlights',
  NOTES: 'bible_reader_notes',
  READING_HISTORY: 'bible_reader_history',
  SEEN_VERSES: 'bible_reader_seen_verses'
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Bible data request failed (${response.status})`);
  }
  return response.json();
};

const requireTranslation = (translationId) => {
  const normalizedId = String(translationId || DEFAULT_BIBLE_TRANSLATION).toUpperCase();
  const translation = getBibleTranslation(normalizedId);
  if (!translation) {
    throw new Error(`Bible translation "${normalizedId}" is not installed`);
  }
  return translation;
};

export const getBibleManifest = async (translationId = DEFAULT_BIBLE_TRANSLATION) => {
  const translation = requireTranslation(translationId);
  if (!manifestCache.has(translation.id)) {
    manifestCache.set(
      translation.id,
      fetchJson(`${translation.publicPath}/manifest.json`).then((manifest) => {
        if (
          manifest.schemaVersion !== 1 ||
          manifest.translation?.id !== translation.id ||
          manifest.translation?.license !== translation.license ||
          !manifest.totals?.books ||
          !manifest.totals?.chapters ||
          !manifest.totals?.verses
        ) {
          throw new Error(`Invalid Bible manifest for ${translation.name}`);
        }
        return manifest;
      })
    );
  }
  return manifestCache.get(translation.id);
};

const getBookData = async (bookId, translationId) => {
  const translation = requireTranslation(translationId);
  const cacheKey = `${translation.id}-${bookId}`;
  if (!bookCache.has(cacheKey)) {
    bookCache.set(cacheKey, fetchJson(`${translation.publicPath}/${bookId}.json`));
  }
  return bookCache.get(cacheKey);
};

export const getChapter = async (bookId, chapter, translationId = DEFAULT_BIBLE_TRANSLATION) => {
  const normalizedBookId = String(bookId || '').toUpperCase();
  const normalizedChapter = Number(chapter);
  const translation = requireTranslation(translationId);

  if (!chapterExists(normalizedBookId, normalizedChapter)) {
    throw new Error(`Unknown Bible chapter: ${normalizedBookId} ${chapter}`);
  }

  const bookData = await getBookData(normalizedBookId, translation.id);
  if (
    bookData.schemaVersion !== 1 ||
    bookData.translationId !== translation.id ||
    bookData.book?.id !== normalizedBookId
  ) {
    throw new Error(`Invalid Bible dataset for ${translation.name}`);
  }

  const chapterData = bookData.chapters.find((item) => item.number === normalizedChapter);
  if (!chapterData || !chapterData.verses?.length) {
    throw new Error(`${translation.name} does not contain ${normalizedBookId} ${normalizedChapter}`);
  }

  addToReadingHistory(normalizedBookId, normalizedChapter, translation.id);
  return {
    book: normalizedBookId,
    chapter: normalizedChapter,
    version: translation.id,
    translation,
    source: 'validated-local-dataset',
    verses: chapterData.verses
  };
};

const readStorageArray = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : [];
  } catch (error) {
    console.error(`Error reading ${key}:`, error);
    return [];
  }
};

const writeStorageArray = (key, items) => {
  localStorage.setItem(key, JSON.stringify(items));
  return items;
};


const normalizeSearchText = (value) =>
  String(value || '').toLowerCase().replace(/[^a-z0-9\s:]/g, ' ').replace(/\s+/g, ' ').trim();

export const parseBibleReference = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, ' ');
  for (const book of [...bibleStructure].sort((a, b) => b.name.length - a.name.length)) {
    const escapedName = book.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const alias = escapedName.replace(/\s+/g, '\\s+');
    const match = normalized.match(new RegExp(`^${alias}\\s+(\\d+)(?::(\\d+))?$`, 'i'));
    if (!match) continue;
    const chapter = Number(match[1]);
    const verse = match[2] ? Number(match[2]) : null;
    if (chapterExists(book.id, chapter)) return { book: book.id, chapter, verse };
  }
  return null;
};

// Reading cards can cite a range; open its first verse in the standalone reader.
export const getBibleReaderPath = (reference) => {
  const startReference = String(reference || '').trim().replace(/[–—-]\s*\d+(?::\d+)?$/, '');
  const passage = parseBibleReference(startReference.replace(/^Psalm\s/i, 'Psalms '));
  if (!passage) return '/bible/reader';
  return `/bible/reader/${passage.book}?chapter=${passage.chapter}${passage.verse ? `&verse=${passage.verse}` : ''}`;
};

export const searchBible = async (query, translationId = DEFAULT_BIBLE_TRANSLATION, limit = 50) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];
  const reference = parseBibleReference(query);
  const terms = normalizedQuery.split(' ').filter((term) => term.length > 1 && !/^\d+$/.test(term));
  const results = [];

  for (const book of bibleStructure) {
    const bookData = await getBookData(book.id, translationId);
    for (const chapter of bookData.chapters || []) {
      if (reference && (reference.book !== book.id || reference.chapter !== chapter.number)) continue;
      for (const verse of chapter.verses || []) {
        if (verse.omitted || !verse.text) continue;
        if (reference?.verse && reference.verse !== verse.number) continue;
        const verseText = normalizeSearchText(verse.text);
        const referenceText = normalizeSearchText(`${book.name} ${chapter.number}:${verse.number}`);
        const exactPhrase = verseText.includes(normalizedQuery);
        const matchingTerms = terms.filter((term) => verseText.includes(term));
        const referenceScore = reference ? 100 : referenceText.includes(normalizedQuery) ? 80 : 0;
        const textScore = exactPhrase ? 50 : matchingTerms.length * 12;
        if (referenceScore || textScore) {
          results.push({
            id: `${book.id}-${chapter.number}-${verse.number}`,
            book: book.id,
            bookName: book.name,
            chapter: chapter.number,
            verse: verse.number,
            text: verse.text,
            version: requireTranslation(translationId).id,
            reference: `${book.name} ${chapter.number}:${verse.number}`,
            score: referenceScore + textScore
          });
        }
      }
    }
  }
  return results.sort((a, b) => b.score - a.score).slice(0, limit);
};

export const getSeenVerses = () => readStorageArray(STORAGE_KEYS.SEEN_VERSES);

export const markVerseSeen = (book, chapter, verse, version = DEFAULT_BIBLE_TRANSLATION, source = 'center-scroll') => {
  const id = `${book}-${chapter}-${verse}-${version}`;
  const seen = getSeenVerses();
  if (!seen.some((item) => item.id === id)) {
    seen.push({ id, book, chapter, verse, version, source, timestamp: new Date().toISOString() });
    writeStorageArray(STORAGE_KEYS.SEEN_VERSES, seen);
  }
  return seen;
};

export const getReadingHeatmap = (version = DEFAULT_BIBLE_TRANSLATION) => {
  const counts = new Map();
  getSeenVerses().filter((item) => item.version === version).forEach((item) => {
    counts.set(item.book, (counts.get(item.book) || 0) + 1);
  });
  return bibleStructure.map((book) => ({
    book: book.id,
    name: getBookName(book.id),
    seen: counts.get(book.id) || 0,
    chapters: book.chapters
  }));
};

export const getBookmarks = () => readStorageArray(STORAGE_KEYS.BOOKMARKS);

export const addBookmark = async (book, chapter, verse = null, version = DEFAULT_BIBLE_TRANSLATION) => {
  const bookmarks = getBookmarks();
  const bookName = bibleStructure.find((item) => item.id === book)?.name || book;
  const bookmark = {
    id: `${book}-${chapter}-${verse || 'chapter'}-${version}`,
    book,
    chapter,
    verse,
    version,
    reference: verse ? `${bookName} ${chapter}:${verse}` : `${bookName} ${chapter}`,
    timestamp: new Date().toISOString()
  };
  if (!bookmarks.some((item) => item.id === bookmark.id)) bookmarks.unshift(bookmark);
  return writeStorageArray(STORAGE_KEYS.BOOKMARKS, bookmarks);
};

export const removeBookmark = async (bookmarkId) =>
  writeStorageArray(STORAGE_KEYS.BOOKMARKS, getBookmarks().filter((item) => item.id !== bookmarkId));

export const getHighlights = () => readStorageArray(STORAGE_KEYS.HIGHLIGHTS);

export const addHighlight = async (book, chapter, verse, color = 'yellow', version = DEFAULT_BIBLE_TRANSLATION) => {
  const highlights = getHighlights();
  const id = `${book}-${chapter}-${verse}-${version}`;
  const highlight = { id, book, chapter, verse, color, version, timestamp: new Date().toISOString() };
  const existingIndex = highlights.findIndex((item) => item.id === id);
  if (existingIndex >= 0) highlights[existingIndex] = highlight;
  else highlights.push(highlight);
  return writeStorageArray(STORAGE_KEYS.HIGHLIGHTS, highlights);
};

export const removeHighlight = async (highlightId) =>
  writeStorageArray(STORAGE_KEYS.HIGHLIGHTS, getHighlights().filter((item) => item.id !== highlightId));

export const getNotes = () => readStorageArray(STORAGE_KEYS.NOTES);

export const addNote = async (book, chapter, verse, text, version = DEFAULT_BIBLE_TRANSLATION) => {
  const notes = getNotes();
  const id = `${book}-${chapter}-${verse}-${version}`;
  const note = { id, book, chapter, verse, text, version, timestamp: new Date().toISOString() };
  const existingIndex = notes.findIndex((item) => item.id === id);
  if (existingIndex >= 0) notes[existingIndex] = note;
  else notes.push(note);
  return writeStorageArray(STORAGE_KEYS.NOTES, notes);
};

export const removeNote = async (noteId) =>
  writeStorageArray(STORAGE_KEYS.NOTES, getNotes().filter((item) => item.id !== noteId));

export const getRecentReadings = (limit = 20) =>
  readStorageArray(STORAGE_KEYS.READING_HISTORY).slice(0, limit);

export const getLastReading = () => getRecentReadings(1)[0] || null;

const addToReadingHistory = (book, chapter, version = DEFAULT_BIBLE_TRANSLATION) => {
  const history = getRecentReadings(100);
  const bookName = bibleStructure.find((item) => item.id === book)?.name || book;
  const reading = {
    id: `${book}-${chapter}-${version}-${Date.now()}`,
    book,
    chapter,
    version,
    reference: `${bookName} ${chapter}`,
    timestamp: new Date().toISOString()
  };
  const uniqueHistory = history.filter(
    (item) => !(item.book === book && item.chapter === chapter && item.version === version)
  );
  return writeStorageArray(STORAGE_KEYS.READING_HISTORY, [reading, ...uniqueHistory].slice(0, 100));
};

export default {
  getBibleManifest,
  getChapter,
  getBookmarks,
  addBookmark,
  removeBookmark,
  getHighlights,
  addHighlight,
  removeHighlight,
  getNotes,
  addNote,
  removeNote,
  getRecentReadings,
  getLastReading,
  searchBible,
  parseBibleReference,
  getSeenVerses,
  markVerseSeen,
  getReadingHeatmap
};
